import { UrlMatchResult, UrlMatcher, UrlSegment } from '@angular/router';

/**
 * Shape of the token in a branded customer link, fixed by the API that mints it: base62,
 * exactly 10 characters. No literal Linx route has this shape, so a token can never shadow one.
 */
const LINK_TOKEN_PATTERN = /^[0-9a-z]{10}$/i;

export const isLinkToken = (segment: string): boolean => LINK_TOKEN_PATTERN.test(segment);

/**
 * Builds the matcher for a branded customer link — `/<token>`, or `/<slug>/<token>` where the
 * slug is the account's cosmetic name. Only the token identifies the flow, so only the token
 * is exposed as a param.
 *
 * `reservedSlugs` are the app's own top-level paths, passed in by `app.routes.ts` from the
 * route table itself so the two cannot drift. They are needed because Angular's recogniser
 * backtracks: when a literal route matches its own segment but nothing deeper does, it moves
 * on to the routes below — so without them `/linx/<10 chars>` would be read as a slugged
 * branded link and resolve a junk token instead of falling through to the wildcard.
 */
export const createLinkTokenMatcher =
  (reservedSlugs: string[]): UrlMatcher =>
  (segments: UrlSegment[]): UrlMatchResult | null => {
    if (segments.length < 1 || segments.length > 2) {
      return null;
    }

    if (segments.length === 2 && reservedSlugs.includes(segments[0].path)) {
      return null;
    }

    const token = segments[segments.length - 1];

    return isLinkToken(token.path) ? { consumed: segments, posParams: { token } } : null;
  };
